import { logger } from '../../utils/logger';
import {
  Context,
  ContextItem,
  ContextType,
  OptimizationStrategy,
  ContextConfig
} from './types';

// 优化器配置
export interface OptimizerConfig extends Pick<ContextConfig, 
  'maxSize' | 'maxItems' | 'relevanceThreshold'> {
  strategy?: OptimizationStrategy;
  preserveTypes?: ContextType[];
  weights?: {
    relevance?: number;
    recency?: number;
    priority?: number;
    size?: number;
  };
}

// 上下文优化器
export class ContextOptimizer {
  private config: Required<OptimizerConfig>;
  
  constructor(config: OptimizerConfig = {}) {
    this.config = {
      maxSize: 100 * 1024,
      maxItems: 100,
      relevanceThreshold: 0.3,
      strategy: OptimizationStrategy.HYBRID,
      preserveTypes: [ContextType.SYSTEM, ContextType.WORKFLOW],
      weights: {
        relevance: 0.4,
        recency: 0.2,
        priority: 0.3,
        size: 0.1
      },
      ...config
    };
  }
  
  /**
   * 优化上下文
   */
  async optimize(context: Context): Promise<Context> {
    const startTime = Date.now();
    logger.debug('Optimizing context:', {
      id: context.id,
      itemCount: context.items.length,
      totalSize: context.totalSize
    });
    
    try {
      // 如果不需要优化，直接返回
      if (context.items.length <= this.config.maxItems &&
          context.totalSize <= this.config.maxSize) {
        return context;
      }
      
      // 根据策略优化
      let optimizedItems: ContextItem[];
      
      switch (this.config.strategy) {
        case OptimizationStrategy.RELEVANCE:
          optimizedItems = await this.optimizeByRelevance(context.items);
          break;
        case OptimizationStrategy.RECENCY:
          optimizedItems = await this.optimizeByRecency(context.items);
          break;
        case OptimizationStrategy.PRIORITY:
          optimizedItems = await this.optimizeByPriority(context.items);
          break;
        case OptimizationStrategy.SIZE:
          optimizedItems = await this.optimizeBySize(context.items);
          break;
        case OptimizationStrategy.HYBRID:
        default:
          optimizedItems = await this.optimizeHybrid(context.items);
      }
      
      // 确保保留必要的类型
      optimizedItems = this.ensurePreservedTypes(
        optimizedItems,
        context.items
      );
      
      // 限制项目数和大小
      optimizedItems = this.enforceConstraints(optimizedItems);
      
      const optimizedContext: Context = {
        ...context,
        items: optimizedItems,
        totalSize: this.calculateTotalSize(optimizedItems),
        metadata: {
          ...context.metadata,
          optimized: true,
          optimizationStrategy: this.config.strategy,
          originalItemCount: context.items.length,
          originalSize: context.totalSize
        }
      };
      
      const duration = Date.now() - startTime;
      logger.info(`Context optimized in ${duration}ms:`, {
        originalItems: context.items.length,
        optimizedItems: optimizedItems.length,
        originalSize: context.totalSize,
        optimizedSize: optimizedContext.totalSize,
        reduction: `${Math.round((1 - optimizedContext.totalSize / context.totalSize) * 100)}%`
      });
      
      return optimizedContext;
      
    } catch (error) {
      logger.error('Failed to optimize context:', error);
      return context; // 失败时返回原始上下文
    }
  }
  
  /**
   * 计算项目得分
   */
  calculateScore(item: ContextItem): number {
    const weights = this.config.weights!;
    const now = Date.now();
    
    // 相关性得分 (0-1)
    const relevanceScore = item.relevance || 0.5;
    
    // 时间衰减得分 (0-1)
    const ageInMinutes = (now - item.timestamp) / (1000 * 60);
    const recencyScore = Math.exp(-ageInMinutes / 60); // 1小时半衰期
    
    // 优先级得分 (0-1)
    const priorityScore = item.priority / 100;
    
    // 大小得分 (0-1，越小越好)
    const sizeScore = 1 - Math.min((item.size || 0) / 10000, 1);
    
    // 加权总分
    return (
      relevanceScore * weights.relevance! +
      recencyScore * weights.recency! +
      priorityScore * weights.priority! +
      sizeScore * weights.size!
    );
  }
  
  /**
   * 基于相关性优化
   */
  private async optimizeByRelevance(items: ContextItem[]): Promise<ContextItem[]> {
    return items
      .filter(item => (item.relevance || 0) >= this.config.relevanceThreshold)
      .sort((a, b) => (b.relevance || 0) - (a.relevance || 0));
  }
  
  /**
   * 基于时间优化
   */
  private async optimizeByRecency(items: ContextItem[]): Promise<ContextItem[]> {
    return items.sort((a, b) => b.timestamp - a.timestamp);
  }
  
  /**
   * 基于优先级优化
   */
  private async optimizeByPriority(items: ContextItem[]): Promise<ContextItem[]> {
    return items.sort((a, b) => b.priority - a.priority);
  }
  
  /**
   * 基于大小优化
   */
  private async optimizeBySize(items: ContextItem[]): Promise<ContextItem[]> {
    // 保留小的项目
    return items.sort((a, b) => (a.size || 0) - (b.size || 0));
  }
  
  /**
   * 混合策略优化
   */
  private async optimizeHybrid(items: ContextItem[]): Promise<ContextItem[]> {
    // 计算每个项目的综合得分
    const scoredItems = items.map(item => ({
      item,
      score: this.calculateScore(item)
    }));
    
    // 按得分排序
    scoredItems.sort((a, b) => b.score - a.score);
    
    // 返回排序后的项目
    return scoredItems.map(({ item }) => item);
  }
  
  /**
   * 确保保留必要的类型
   */
  private ensurePreservedTypes(
    optimizedItems: ContextItem[],
    originalItems: ContextItem[]
  ): ContextItem[] {
    const result = [...optimizedItems];
    const includedIds = new Set(result.map(item => item.id));
    
    // 检查每个需要保留的类型
    for (const type of this.config.preserveTypes!) {
      const hasType = result.some(item => item.type === type);
      
      if (!hasType) {
        // 从原始项目中找到该类型的最高优先级项
        const typeItems = originalItems
          .filter(item => item.type === type && !includedIds.has(item.id))
          .sort((a, b) => b.priority - a.priority);
        
        if (typeItems.length > 0) {
          result.unshift(typeItems[0]);
          includedIds.add(typeItems[0].id);
        }
      }
    }
    
    return result;
  }
  
  /**
   * 强制执行约束
   */
  private enforceConstraints(items: ContextItem[]): ContextItem[] {
    const result: ContextItem[] = [];
    let totalSize = 0;
    
    for (const item of items) {
      const itemSize = item.size || 0;
      
      // 检查约束
      if (result.length >= this.config.maxItems ||
          totalSize + itemSize > this.config.maxSize) {
        break;
      }
      
      result.push(item);
      totalSize += itemSize;
    }
    
    return result;
  }
  
  /**
   * 计算总大小
   */
  private calculateTotalSize(items: ContextItem[]): number {
    return items.reduce((total, item) => total + (item.size || 0), 0);
  }
  
  /**
   * 分析优化效果
   */
  analyzeOptimization(
    original: Context,
    optimized: Context
  ): {
    itemReduction: number;
    sizeReduction: number;
    avgRelevance: number;
    preservedTypes: ContextType[];
    removedTypes: ContextType[];
  } {
    const originalTypes = new Set(original.items.map(i => i.type));
    const optimizedTypes = new Set(optimized.items.map(i => i.type));
    
    const removedTypes = Array.from(originalTypes).filter(
      type => !optimizedTypes.has(type)
    );
    
    const avgRelevance = optimized.items.reduce(
      (sum, item) => sum + (item.relevance || 0),
      0
    ) / optimized.items.length;
    
    return {
      itemReduction: 1 - optimized.items.length / original.items.length,
      sizeReduction: 1 - optimized.totalSize / original.totalSize,
      avgRelevance,
      preservedTypes: Array.from(optimizedTypes),
      removedTypes
    };
  }
  
  /**
   * 推荐优化配置
   */
  recommendConfig(
    context: Context
  ): Partial<OptimizerConfig> {
    const items = context.items;
    
    // 分析上下文特征
    const avgRelevance = items.reduce(
      (sum, item) => sum + (item.relevance || 0),
      0
    ) / items.length;
    
    const typeDistribution = new Map<ContextType, number>();
    for (const item of items) {
      typeDistribution.set(
        item.type,
        (typeDistribution.get(item.type) || 0) + 1
      );
    }
    
    // 根据特征推荐配置
    const recommendations: Partial<OptimizerConfig> = {};
    
    // 如果平均相关性低，提高阈值
    if (avgRelevance < 0.5) {
      recommendations.relevanceThreshold = 0.4;
    }
    
    // 如果知识项目过多，使用相关性策略
    const knowledgeCount = typeDistribution.get(ContextType.KNOWLEDGE) || 0;
    if (knowledgeCount > items.length * 0.5) {
      recommendations.strategy = OptimizationStrategy.RELEVANCE;
    }
    
    // 如果历史项目过多，使用时间策略
    const historyCount = typeDistribution.get(ContextType.HISTORY) || 0;
    if (historyCount > items.length * 0.5) {
      recommendations.strategy = OptimizationStrategy.RECENCY;
    }
    
    // 根据大小调整权重
    if (context.totalSize > this.config.maxSize * 1.5) {
      recommendations.weights = {
        ...this.config.weights,
        size: 0.3,
        relevance: 0.5
      };
    }
    
    return recommendations;
  }
}